import { Injectable } from '@angular/core';
import { HttpProvider } from '../http/http';
import { APP_CONFIG, Region } from '../../environment';
import { IterableClickMsgInAppRequest, IterableOpenMsgInAppRequest, IterableInAppRequest, PlatformName } from '../../model/api/iterables/iterables-inapp-request.entity.interface';
import { InAppMessage, IterableInAppResponse, IterableOpenMsgInAppResponse, RESPONSE_STATUS } from '../../model/api/iterables/iterables-inapp-response.entity.interface';
import { LoggerEvent, LoggerProvider } from '../logger/logger'
import { RouteProvider } from '../route/route';
import { Platform } from 'ionic-angular/platform/platform';
import { TranslateService } from '@ngx-translate/core';
import { SegmentProvider } from '../analytics/segment';
import { AnalyticsEventType, ICarouselCTAClicked } from '../../model/types/analytics.const';
import { ConfigProvider } from '../config/config';
import { CustomerProvider } from '../customer/customer';


@Injectable()
export class IterableInAppProvider {
 
  email;
  notificationTimer;
  showViewAllBtnForNonPriorityMsg: boolean = false;
  
  constructor(private httpProvider: HttpProvider,
    private routeProvider: RouteProvider,
    private platform: Platform,
    private translate: TranslateService,
    private analytics: SegmentProvider,
    private customerProvider: CustomerProvider) {
  }

  async init() {
    try{
      // SN always uses the placeholder identifier so SDK push registrationn target the same Iterable recordd
      if (APP_CONFIG.region.region === Region.SN) {
        const customerId = this.httpProvider.getAuthCustomerId() || ('U' + this.httpProvider.getAuthUserId());
        this.email = customerId ? customerId + '@placeholder.email' : null;
        return;
      }
      const authEmail = this.httpProvider.getAuthEmail();
      if (authEmail) {
        this.email = authEmail;
        return;
      }
        const customer = await this.customerProvider.getCustomer();
      if (customer && customer.emailAddress) {
        this.email = customer.emailAddress;
        return;
      }
    } catch(ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Could not get user email');
    }

  }

  async getInAppMessages(queryParam: string, customHeader: any): Promise<IterableInAppResponse> {
    LoggerProvider.info(this, "getInAppMessages");
    let response: IterableInAppResponse = { status: RESPONSE_STATUS.SUCCESS, inAppMessages: [] };
    try{
      const res: IterableInAppResponse = await this.httpProvider.getExtern(`${APP_CONFIG.ITERABLE_INAPP_API}${queryParam}`, null, customHeader, null, true);
      response.status = res.status;
      response.inAppMessages = res.inAppMessages;
      return response;
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.ProviderException, "getInAppMessages failed", ex);
    }
  }

  async prepareAppMessageRequest(): Promise<InAppMessage[]> {
    await this.init();
    if (!this.email) return;
    const request: IterableInAppRequest = {
      email:  this.email,
      count: 30,
      platform: this.platform.is('ios')? PlatformName.IOS : PlatformName.ANDROID
    }

    const queryParam = `api/inApp/getMessages?email=${request.email}&count=${request.count}&platform=${request.platform}`
    const customHeader = { "Api-Key": APP_CONFIG.ITERABLE_INAPP_API_KEY };
    const inAppMessageResp = await this.getInAppMessages(queryParam, customHeader);
    if (inAppMessageResp) {
      return inAppMessageResp.inAppMessages;
    }
    return null;
  }

  async getAppMessages(): Promise<InAppMessage[]> {
    let appMessages:  Array<InAppMessage> = [];
    const inAppMessages = await this.prepareAppMessageRequest();
    if (inAppMessages) {
      appMessages = inAppMessages.filter(message => message.saveToInbox === true);
    }
    return appMessages;
  }

  getMessagegNonPriorityViewAllBtn() {
    return this.showViewAllBtnForNonPriorityMsg;
  }

  async getAppActivityMessages(RmtSlides: any): Promise<InAppMessage[]> {
    let appMessages:  Array<InAppMessage> = [];

    const inAppMessages = await this.prepareAppMessageRequest();
    if (inAppMessages) {
      appMessages = inAppMessages.filter(message => {
        const removedExists = RmtSlides.removedSlideArray.some(sliderMessage => sliderMessage.messageId === message.messageId);
        let showAppMessage: boolean = true;
        if(message.saveToInbox === true) {
          if(message.read === true) {
            showAppMessage = false;
          }
          
          if(showAppMessage && message.customPayload && message.customPayload.priorityLevel) {
            const priorityLevel = parseInt(message.customPayload.priorityLevel)
            const hasPriority = !Number.isNaN(priorityLevel) && priorityLevel < 6;
            showAppMessage = hasPriority ? true : false;
          } else {
            showAppMessage = false;
          }
          if(!showAppMessage && message.read === false) {
            this.showViewAllBtnForNonPriorityMsg = true;
          }
          if (removedExists) {
            showAppMessage = false;
          }
        } else {
          showAppMessage = false;
        }
        return showAppMessage;
      });
    }

    return appMessages.length > 5 ? appMessages.slice(0, 5).sort((a, b)=> a.customPayload.priorityLevel - b.customPayload.priorityLevel) : appMessages;
  }

  async getInAppMessageUnreadCount(): Promise<number> {
    let count = 0;
    const inAppMessages = await this.prepareAppMessageRequest();
    if (this.email && inAppMessages) {
        const unread: Array<InAppMessage> = inAppMessages.filter(message => message.read === false && message.saveToInbox === true);
        count = unread.length;
    }
    return count;
  }

  async postInAppMessageOpen(messageId: string): Promise<IterableOpenMsgInAppResponse> {
    LoggerProvider.info(this, 'postInAppMessageOpen');
    const customHeader = { 'Api-Key': APP_CONFIG.ITERABLE_INAPP_API_KEY };
    const queryParam = 'api/events/trackInAppOpen';
    let response: IterableOpenMsgInAppResponse = { code: "success", msg: "" };

    const body: IterableOpenMsgInAppRequest = {
      email: this.email,
      messageId: messageId
    }

    try {
      const res: IterableOpenMsgInAppResponse = await this.httpProvider.postExtern(`${APP_CONFIG.ITERABLE_INAPP_API}${queryParam}`, body, customHeader, 'json');
      response.code = res.code;
      response.msg = res.msg;
      return response;
    } catch(ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Could not set the status of the in-app message');
    }
  }

  // Iterable only counts a click if we report it.
  async postInAppMessageClick(messageId: string, clickedUrl: string): Promise<IterableOpenMsgInAppResponse> {
    LoggerProvider.info(this, 'postInAppMessageClick');
    const customHeader = { 'Api-Key': APP_CONFIG.ITERABLE_INAPP_API_KEY };
    const queryParam = 'api/events/trackInAppClick';
    let response: IterableOpenMsgInAppResponse = { code: "success", msg: "" };

    const body: IterableClickMsgInAppRequest = {
      email: this.email,
      messageId: messageId,
      clickedUrl: clickedUrl
    }

    try {
      const res: IterableOpenMsgInAppResponse = await this.httpProvider.postExtern(`${APP_CONFIG.ITERABLE_INAPP_API}${queryParam}`, body, customHeader, 'json');
      response.code = res.code;
      response.msg = res.msg;
      return response;
    } catch(ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Could not report the in-app message click');
    }
  }

  trackCarouselCTAClicked(data: ICarouselCTAClicked) {
    this.analytics.trackEvent({
      eventName: AnalyticsEventType.CarouselCTAClicked,
      analyticsData: {
        campaignId: data.campaignId ? data.campaignId : '',
        carouselCardType: data.cardType,
        entryPoint: data.entryPoint,
        lineNumber: data.lineNumber ? data.lineNumber : 0,
        messageId: data.messageId ? data.messageId : '',
        url: data.url,
      }
    }, true);
  }

  trackCTAClick(data: ICarouselCTAClicked) {
    this.trackCarouselCTAClicked(data);
    if (data.messageId) {
      this.postInAppMessageClick(data.messageId, data.url);
    }
  }

  openMobilAppInbox(heder, location,settingsInboxIcon=null){
    this.routeProvider.openModal(this.routeProvider.pages.IterablesInAppPage, null, ()=>{this.checkInAppNotification(heder,settingsInboxIcon)});
    this.analytics.trackEvent({ eventName: AnalyticsEventType.InAppMessagesAccessed, analyticsData: {
      location: location,
    }});
  }

  async checkInAppNotification(header,settingsInboxIcon=null) {
    
    if(settingsInboxIcon)
      this.updateSettingsCount(settingsInboxIcon);

    if(this.notificationTimer)
      clearInterval(this.notificationTimer);
    this.notificationTimer = null;

    this.notificationTimer = setInterval(async () => {
      if(settingsInboxIcon)
        this.updateSettingsCount(settingsInboxIcon);
    }, ConfigProvider.settings.inAppNotificationTimer);
  }

  async updateInAppCount(header) {
    const unreadNotificationCount = await this.getInAppMessageUnreadCount();
    if (unreadNotificationCount > 0) {
      header.rightButtonType = 'unread_bell';
    } else {
      header.rightButtonType = 'bell';
    }
  }

  async updateSettingsCount(settingsInboxIcon){
    const unreadNotificationCount = await this.getInAppMessageUnreadCount();
    if (unreadNotificationCount > 0) {
      settingsInboxIcon.name = 'custom-icon-settings-inbox-unread';
    } else {
      settingsInboxIcon.name = 'custom-icon-settings-inbox';
    }
  }

  clearNotificationTimer(){
    if (this.notificationTimer)
      clearInterval(this.notificationTimer);
    this.notificationTimer = null;
  }
}



// WEBPACK FOOTER //
// ./src/providers/marketing/iterable-inapp.ts